import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, ArrowLeft, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import { toast } from "sonner";
import LanguageSwitcher from "../i18n/LanguageSwitcher";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";

const LOGIN_IMAGE =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmFsJTIwZGV0YWlsc3xlbnwwfHx8fDE3ODA2NjQyNzd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, register, forgotPassword, verify2fa } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  // Deadline mirroring the backend lg_mfa pre-auth token TTL (5 min), set the
  // moment login returns mfa_required. Lets us show a clear "session expired"
  // message on submit instead of a generic invalid-code error.
  const [mfaDeadline, setMfaDeadline] = useState(null);

  // Iter75/76 — accept ?mode=signup|signin|forgot from the URL query. Landing
  // page and other public entry points deep-link into the signup form.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const q = (params.get("mode") || "").toLowerCase();
    if (q === "signup" || q === "signin" || q === "forgot") {
      setMode(q);
    }
  }, [location.search]);

  if (user && user !== false) {
    const pendingToken = typeof window !== "undefined" ? sessionStorage.getItem("pendingInvitationToken") : null;
    if (pendingToken) {
      sessionStorage.removeItem("pendingInvitationToken");
      return <Navigate to={`/invitations#token=${encodeURIComponent(pendingToken)}`} replace />;
    }
    const dest = location.state?.from || "/dashboard";
    return <Navigate to={dest} replace />;
  }

  const cfg = {
    title: t(`auth:${mode}.title`),
    subtitle: t(`auth:${mode}.subtitle`),
    cta: t(`auth:${mode}.cta`),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t("auth:errors.enter_email"));
      return;
    }
    if (mode !== "forgot" && (!password || password.length < 8)) {
      toast.error(t("auth:errors.password_min"));
      return;
    }
    if (mode === "signup" && password !== confirmPassword) {
      toast.error(t("auth:errors.password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword({ email: email.trim() });
        toast.success(t("auth:forgot.success_toast"));
        setMode("signin");
      } else if (mode === "signup") {
        if (!termsAccepted) {
          toast.error(t("auth:signup.terms_required"));
          setLoading(false);
          return;
        }
        await register({
          email: email.trim(),
          password,
          name: name.trim() || undefined,
          terms_accepted: true,
        });
        toast.success(t("auth:signup.success_toast"));
        const pendingToken = sessionStorage.getItem("pendingInvitationToken");
        if (pendingToken) {
          sessionStorage.removeItem("pendingInvitationToken");
          navigate(`/invitations#token=${encodeURIComponent(pendingToken)}`, { replace: true });
          return;
        }
        navigate(location.state?.from || "/dashboard", { replace: true });
      } else {
        const res = await login({ email: email.trim(), password });
        if (res && res.mfa_required) {
          setMfaDeadline(Date.now() + 5 * 60 * 1000);
          setMfaPending(true);
          setLoading(false);
          return;
        }
        toast.success(t("auth:signin.success_toast"));
        const pendingToken = sessionStorage.getItem("pendingInvitationToken");
        if (pendingToken) {
          sessionStorage.removeItem("pendingInvitationToken");
          navigate(`/invitations#token=${encodeURIComponent(pendingToken)}`, { replace: true });
          return;
        }
        navigate(location.state?.from || "/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cancel the 2FA challenge and return to the normal sign-in screen. The
  // pre-auth session is abandoned; the user re-enters their password.
  const cancelMfa = () => {
    setMfaPending(false);
    setMfaCode("");
    setUseBackupCode(false);
    setMfaDeadline(null);
    setPassword("");
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (mfaDeadline && Date.now() >= mfaDeadline) {
      // Pre-auth token (lg_mfa) has expired — a fresh sign-in is required.
      toast.error(t("auth:two_factor.errors.session_expired"));
      cancelMfa();
      return;
    }
    setLoading(true);
    try {
      await verify2fa({ code: mfaCode.trim() });
      toast.success(t("auth:signin.success_toast"));
      const pendingToken = sessionStorage.getItem("pendingInvitationToken");
      if (pendingToken) {
        sessionStorage.removeItem("pendingInvitationToken");
        navigate(`/invitations#token=${encodeURIComponent(pendingToken)}`, { replace: true });
        return;
      }
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth:two_factor.errors.invalid_code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-12 relative">
        <div className="absolute top-5 right-6">
          <LanguageSwitcher variant="compact" />
        </div>
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="mb-8 inline-block hover:opacity-80 transition-opacity" data-testid="login-brand-link" aria-label="QENVA · The Silent Guardian">
            <img
              src="/brand/v2-primary-logo.png"
              alt="QENVA · The Silent Guardian"
              draggable={false}
              className="h-[120px] w-auto block select-none"
              style={{ objectFit: "contain" }}
              data-testid="login-brand-fulllockup"
            />
          </Link>

          {mfaPending ? (
            <form onSubmit={handleMfaSubmit} className="mt-8 space-y-4" data-testid="mfa-form">
              <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight text-zinc-950">
                {t("auth:two_factor.title")}
              </h1>
              <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{t("auth:two_factor.subtitle")}</p>
              {!useBackupCode ? (
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-code" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                    {t("auth:two_factor.code_label")}
                  </Label>
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder={t("auth:two_factor.code_placeholder")}
                    data-testid="mfa-code-input"
                    className="w-full h-11 px-3 rounded-lg border border-zinc-200 text-sm tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-zinc-300"
                    required
                    autoFocus
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-backup" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                    {t("auth:two_factor.backup_label")}
                  </Label>
                  <input
                    id="mfa-backup"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder={t("auth:two_factor.backup_placeholder")}
                    data-testid="mfa-backup-input"
                    className="w-full h-11 px-3 rounded-lg border border-zinc-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-zinc-300"
                    required
                    autoFocus
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="mfa-verify-button"
                className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("common:actions.please_wait") : t("auth:two_factor.verify_cta")}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode((v) => !v);
                  setMfaCode("");
                }}
                data-testid="mfa-toggle-backup"
                className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                {useBackupCode ? t("auth:two_factor.use_totp") : t("auth:two_factor.use_backup")}
              </button>
              <div className="pt-1">
                <button
                  type="button"
                  onClick={cancelMfa}
                  data-testid="mfa-back-to-signin"
                  className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  <ArrowLeft size={14} /> {t("auth:two_factor.back_to_signin")}
                </button>
              </div>
            </form>
          ) : (
          <>
          {mode === "forgot" && (
            <button
              onClick={() => setMode("signin")}
              data-testid="back-to-signin-button"
              className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
            >
              <ArrowLeft size={14} /> {t("auth:forgot.back_to_signin")}
            </button>
          )}

          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight text-zinc-950">
            {cfg.title}
          </h1>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{cfg.subtitle}</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                  {t("auth:fields.name")}
                </Label>
                <div className="relative">
                  <UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="name"
                    type="text"
                    data-testid="name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("auth:fields.name_placeholder")}
                    className="pl-9 h-11 bg-white"
                    autoComplete="name"
                  />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                {t("auth:fields.email")}
              </Label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth:fields.email_placeholder")}
                  className="pl-9 h-11 bg-white"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {mode !== "forgot" && (
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                  {t("auth:fields.password")}
                </Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth:fields.password_placeholder")}
                    className="pl-9 h-11 bg-white"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    minLength={8}
                    required
                  />
                </div>
                {mode === "signup" && (
                  <p className="text-[11px] text-zinc-400 mt-1">{t("auth:signup.password_hint")}</p>
                )}
                {mode === "signup" && <PasswordStrengthMeter password={password} />}
              </div>
            )}

            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                  {t("auth:fields.confirm_password")}
                </Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("auth:fields.confirm_password_placeholder")}
                    className="pl-9 h-11 bg-white"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
                {confirmPassword.length > 0 && confirmPassword !== password && (
                  <p className="text-[11px] text-rose-700 mt-1" data-testid="confirm-password-error">
                    {t("auth:errors.password_mismatch")}
                  </p>
                )}
              </div>
            )}

            {mode === "signin" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  data-testid="forgot-password-link"
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  {t("auth:signin.forgot_link")}
                </button>
              </div>
            )}

            {/* Iter76 — Consentement CGU obligatoire à l'inscription. */}
            {mode === "signup" && (
              <label
                className="flex items-start gap-2.5 pt-1 select-none cursor-pointer"
                data-testid="signup-terms-label"
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  data-testid="signup-terms-checkbox"
                  aria-required="true"
                  className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-zinc-950 cursor-pointer"
                />
                <span
                  className="text-[12.5px] text-zinc-600 leading-relaxed"
                  data-testid="signup-terms-text"
                >
                  <span dangerouslySetInnerHTML={{
                    __html: t("auth:signup.terms_html", {
                      termsUrl: "/legal/terms",
                      privacyUrl: "/legal/privacy",
                      interpolation: { escapeValue: false },
                    })
                  }} />
                </span>
              </label>
            )}

            <Button
              type="submit"
              disabled={loading || (mode === "signup" && !termsAccepted)}
              data-testid="submit-auth-button"
              className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{loading ? t("common:actions.please_wait") : cfg.cta}</span>
              <ArrowRight size={15} className="ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>

          {mode !== "forgot" && (
            <p className="text-sm text-zinc-500 mt-8 text-center">
              {mode === "signin" ? (
                <>
                  {t("auth:signin.no_account")}{" "}
                  <button
                    onClick={() => setMode("signup")}
                    data-testid="switch-to-signup-button"
                    className="text-zinc-950 font-medium hover:underline"
                  >
                    {t("auth:signin.signup_link")}
                  </button>
                </>
              ) : (
                <>
                  {t("auth:signup.have_account")}{" "}
                  <button
                    onClick={() => setMode("signin")}
                    data-testid="switch-to-signin-button"
                    className="text-zinc-950 font-medium hover:underline"
                  >
                    {t("auth:signup.signin_link")}
                  </button>
                </>
              )}
            </p>
          )}
          </>
          )}

          <p className="text-[11px] text-zinc-400 mt-10 leading-relaxed text-center">
            {t("auth:legal")}
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative w-[42%] xl:w-[48%] bg-zinc-100">
        <img
          src={LOGIN_IMAGE}
          alt="Modern office architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="text-[11px] uppercase tracking-[0.22em] opacity-80 mb-3">{t("auth:hero_kicker")}</p>
          <p className="font-display text-2xl xl:text-3xl font-semibold tracking-tight leading-tight max-w-md">
            {t("auth:hero_quote")}
          </p>
        </div>
      </div>
    </div>
  );
}
